import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUp, Shield, Menu, X } from "lucide-react";

const TOC = [
  { id: "what-we-collect",  label: "Information We Collect",  num: "01" },
  { id: "how-we-use",       label: "How We Use Your Data",    num: "02" },
  { id: "cell-number",      label: "Cell Number",             num: "03" },
  { id: "data-sharing",     label: "Data Sharing",            num: "04" },
  { id: "data-retention",   label: "Data Retention",          num: "05" },
  { id: "security",         label: "Security",                num: "06" },
  { id: "your-rights",      label: "Your Rights",             num: "07" },
  { id: "children",         label: "Children''s Privacy",     num: "08" },
  { id: "policy-changes",   label: "Policy Changes",          num: "09" },
  { id: "contact",          label: "Contact",                 num: "10" },
];

function Section({ id, num, title, children }: { id: string; num: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-28 py-8 border-b border-gray-100 last:border-0">
      <div className="flex items-start gap-4 mb-4">
        <span className="flex-shrink-0 mt-0.5 text-xs font-mono font-bold text-[#0277BD] bg-blue-50 border border-blue-100 rounded-md px-2 py-1 tracking-widest">
          {num}
        </span>
        <h2 className="text-xl font-semibold text-gray-900 leading-snug">{title}</h2>
      </div>
      <div className="ml-12 space-y-3">{children}</div>
    </section>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="text-gray-600 leading-7 text-[15px]">{children}</p>;
}

function List({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2.5">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3 text-[15px] text-gray-600 leading-7">
          <span className="mt-2.5 h-1.5 w-1.5 rounded-full bg-[#0277BD] flex-shrink-0" />
          {item}
        </li>
      ))}
    </ul>
  );
}

export default function PrivacyPage() {
  const [activeSection, setActiveSection] = useState<string>("");
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [tocOpen, setTocOpen] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { if (e.isIntersecting) setActiveSection(e.target.id); }); },
      { rootMargin: "-15% 0px -75% 0px" }
    );
    TOC.forEach(({ id }) => { const el = document.getElementById(id); if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onScroll = () => setShowBackToTop(window.scrollY > 500);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">

      {/* Navigation bar */}
      <nav className="sticky top-0 z-30 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="h-9 w-9 rounded-xl bg-[#0277BD] flex items-center justify-center shadow-sm group-hover:bg-[#01579B] transition-colors">
              <span className="text-white font-bold text-lg" style={{ fontFamily: "Georgia, serif" }}>B</span>
            </div>
            <div className="hidden sm:block">
              <div className="text-gray-900 font-bold text-sm leading-none">301io</div>
              <div className="text-gray-400 text-xs leading-none mt-0.5">Home Protection</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/terms" className="hidden sm:block text-sm text-gray-500 hover:text-gray-900 transition-colors px-3 py-1.5 rounded-lg hover:bg-gray-50">
              Terms &amp; Conditions
            </Link>
            <Link to="/auth/login" className="text-sm font-medium text-white bg-[#0277BD] hover:bg-[#01579B] transition-colors px-4 py-2 rounded-lg shadow-sm">
              Sign In
            </Link>
            <button className="lg:hidden p-2 rounded-lg hover:bg-gray-100" onClick={() => setTocOpen(true)}>
              <Menu className="h-5 w-5 text-gray-600" />
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <div className="bg-gradient-to-br from-[#01579B] via-[#0277BD] to-[#0288D1]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="flex items-center gap-2 text-blue-200 text-sm mb-6">
            <Shield className="h-4 w-4" />
            <span>Legal</span>
            <span className="mx-1 opacity-50">/</span>
            <span className="text-white font-medium">Privacy Policy</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 tracking-tight">
            Privacy Policy
          </h1>
          <p className="text-blue-100 text-base sm:text-lg max-w-2xl leading-relaxed">
            We are committed to protecting your personal information. This policy explains what we collect, why, and your rights.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2 text-sm">
              <span className="text-blue-200">Last updated</span>
              <span className="text-white font-medium">April 23, 2026</span>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile TOC drawer */}
      {tocOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setTocOpen(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-72 bg-white shadow-2xl p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <span className="font-semibold text-gray-900">Contents</span>
              <button onClick={() => setTocOpen(false)} className="p-1.5 rounded-lg hover:bg-gray-100">
                <X className="h-5 w-5 text-gray-600" />
              </button>
            </div>
            <nav className="space-y-1">
              {TOC.map(({ id, label, num }) => (
                <a key={id} href={`#${id}`} onClick={() => setTocOpen(false)}
                  className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-gray-50 group">
                  <span className="text-xs font-mono text-gray-400 group-hover:text-[#0277BD] w-6">{num}</span>
                  <span className="text-sm text-gray-700">{label}</span>
                </a>
              ))}
            </nav>
          </div>
        </div>
      )}

      {/* Main layout */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex gap-10 xl:gap-16">

          {/* Sidebar TOC */}
          <aside className="hidden lg:block w-56 xl:w-64 flex-shrink-0" ref={headerRef}>
            <div className="sticky top-24">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-4 px-3">Contents</p>
              <nav className="space-y-0.5">
                {TOC.map(({ id, label, num }) => (
                  <a key={id} href={`#${id}`}
                    className={`flex items-center gap-3 py-2 px-3 rounded-lg text-sm transition-all group ${
                      activeSection === id ? "bg-blue-50 text-[#0277BD] font-medium" : "text-gray-500 hover:text-gray-900 hover:bg-gray-50"
                    }`}>
                    <span className={`text-xs font-mono w-5 flex-shrink-0 ${activeSection === id ? "text-[#0277BD]" : "text-gray-300 group-hover:text-gray-400"}`}>{num}</span>
                    {label}
                  </a>
                ))}
              </nav>
              <div className="mt-8 p-4 rounded-xl bg-gray-50 border border-gray-100">
                <p className="text-xs text-gray-500 leading-relaxed">
                  Privacy questions?{" "}
                  <a href="mailto:privacy@brandsmart.com" className="text-[#0277BD] hover:underline font-medium">Contact us</a>
                </p>
              </div>
            </div>
          </aside>

          {/* Document body */}
          <article className="flex-1 min-w-0 max-w-3xl">

            <div className="mb-10 p-5 rounded-2xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100">
              <p className="text-gray-700 leading-7 text-[15px]">
                301io ("we", "us") is committed to protecting your personal information.
                This Privacy Policy explains what data we collect, why we collect it, and how
                you can exercise your rights.
              </p>
            </div>

            <Section id="what-we-collect" num="01" title="Information We Collect">
              <P>When you register and use 301io we collect:</P>
              <List items={[
                "Identity data: first name, last name, email address, cell number",
                "Home data: address, home type, square footage",
                "Asset data: appliance details, serial numbers, purchase dates, warranties",
                "Usage data: in-app actions, diagnostics requests, service bookings",
                "Device data: device model, OS version, push-notification tokens",
                "Network data: Wi-Fi network names (SSIDs) discovered by the scan feature",
              ]} />
            </Section>

            <Section id="how-we-use" num="02" title="How We Use Your Information">
              <List items={[
                "To create and manage your account",
                "To send OTP codes and transactional SMS/email notifications",
                "To power AI diagnostic recommendations for your appliances",
                "To process payments and manage protection plan claims",
                "To improve product features and fix bugs",
                "To comply with legal obligations",
              ]} />
            </Section>

            <Section id="cell-number" num="03" title="Cell Number">
              <P>
                Your cell number is used solely for account verification (OTP), service booking
                confirmations, and critical maintenance alerts. We do not sell or share your
                cell number with third-party marketers.
              </P>
            </Section>

            <Section id="data-sharing" num="04" title="Data Sharing">
              <P>
                We share data only with trusted service providers (e.g. Stripe for payments,
                AWS for hosting, SendGrid for email) under strict data-processing agreements.
                We do not sell your personal information.
              </P>
            </Section>

            <Section id="data-retention" num="05" title="Data Retention">
              <P>
                We retain your data for as long as your account is active. You may request
                deletion at any time by contacting{" "}
                <a href="mailto:privacy@brandsmart.com" className="text-[#0277BD] hover:underline font-medium">
                  privacy@brandsmart.com
                </a>
                . Certain data may be retained to comply with legal obligations.
              </P>
            </Section>

            <Section id="security" num="06" title="Security">
              <P>
                We use industry-standard encryption (TLS in transit, AES-256 at rest) and
                role-based access controls to protect your data.
              </P>
            </Section>

            <Section id="your-rights" num="07" title="Your Rights">
              <P>Depending on your jurisdiction you have the right to:</P>
              <List items={[
                "Access the personal data we hold about you",
                "Correct inaccurate data",
                "Request deletion of your data (right to be forgotten)",
                "Opt out of marketing communications at any time",
              ]} />
            </Section>

            <Section id="children" num="08" title="Children's Privacy">
              <P>
                301io is not directed at children under 13. We do not knowingly collect
                data from children. If you believe a child has registered, please contact us immediately.
              </P>
            </Section>

            <Section id="policy-changes" num="09" title="Changes to This Policy">
              <P>
                We may update this Policy periodically. We will notify you of material changes
                via email or in-app notification.
              </P>
            </Section>

            <Section id="contact" num="10" title="Contact">
              <P>
                Privacy questions? Email us at{" "}
                <a href="mailto:privacy@brandsmart.com" className="text-[#0277BD] hover:underline font-medium">
                  privacy@brandsmart.com
                </a>
                .
              </P>
              <address className="not-italic mt-3 p-4 rounded-xl bg-gray-50 border border-gray-100 text-sm text-gray-600 leading-7">
                <strong className="text-gray-900 font-semibold">301io</strong><br />
                3200 SW 42nd St<br />
                Fort Lauderdale, FL 33312, USA
              </address>
            </Section>

            {/* Footer */}
            <div className="mt-14 pt-8 border-t border-gray-100">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="h-6 w-6 rounded-md bg-[#0277BD] flex items-center justify-center">
                      <span className="text-white font-bold text-xs" style={{ fontFamily: "Georgia, serif" }}>B</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">301io</span>
                  </div>
                  <p className="text-xs text-gray-400">� {new Date().getFullYear()} All rights reserved.</p>
                </div>
                <div className="flex items-center gap-5 text-sm text-gray-400">
                  <Link to="/terms" className="hover:text-[#0277BD] transition-colors">Terms &amp; Conditions</Link>
                  <Link to="/auth/login" className="hover:text-[#0277BD] transition-colors">Sign In</Link>
                </div>
              </div>
            </div>
          </article>
        </div>
      </div>

      {/* Back to top */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        className={`fixed bottom-6 right-6 z-30 h-11 w-11 rounded-full bg-[#0277BD] text-white shadow-lg flex items-center justify-center hover:bg-[#01579B] transition-all duration-300 ${
          showBackToTop ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
        }`}
        aria-label="Back to top"
      >
        <ArrowUp className="h-4 w-4" />
      </button>
    </div>
  );
}
